import { GetCroppedImgProps, RotateSize } from './types';

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    // OK to load images from a different domain without credentials
    image.src = url;
    image.setAttribute('crossOrigin', 'anonymous');
  });

const urlToFile = (url: string, filename: string, mimeType: string): Promise<File> =>
  fetch(url)
    .then(res => res.arrayBuffer())
    .then(buf => new File([buf], filename, { type: mimeType }));

const getRadianAngle = (degreeValue: number): number => (degreeValue * Math.PI) / 180;

/**
 * Returns the new bounding area of a rotated rectangle.
 */

/**
 * This is a simplified function to remove EXIF data. When an image is
 * converted to a data URL via canvas.toDataURL(), most EXIF metadata
 * is not preserved. This function acts as a pass-through to acknowledge
 * that no further EXIF processing is needed client-side.
 * @param imageDataUrl The Base64 data URL.
 * @returns The same Base64 data URL.
 */
const removeExif = (imageDataUrl: string): string => imageDataUrl;

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * Crops and processes an image with rotation and flip options.
 * The original image data remains unchanged; the function returns a NEW file.
 * @param fileName - Name for the output file
 * @param imageSrc - Source image URL
 * @param pixelCrop - Crop dimensions {x, y, width, height}
 * @param rotation - Rotation angle in degrees
 * @param flip - Flip options {horizontal: boolean, vertical: boolean}
 * @returns Processed image as a File object
 */
export default async function getCroppedImg(props: GetCroppedImgProps): Promise<File | null> {
  try {
    const {
      fileName,
      imageSrc,
      pixelCrop,
      rotation = 0,
      flip = { horizontal: false, vertical: false }
    } = props;
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    const rotRad = getRadianAngle(rotation);

    // Get the cropped image dimensions
    const croppedWidth = pixelCrop.width;
    const croppedHeight = pixelCrop.height;

    // Set canvas size to the final cropped dimensions
    canvas.width = croppedWidth;
    canvas.height = croppedHeight;

    // Translate the canvas context to the center to allow rotation
    ctx.translate(croppedWidth / 2, croppedHeight / 2);
    ctx.rotate(rotRad);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-croppedWidth / 2, -croppedHeight / 2);

    // Draw the image, accounting for the crop and rotation
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      croppedWidth,
      croppedHeight
    );
    
    // The imageSrc format is something like this: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAACgAAAANt....."
    const originalMimeType = imageSrc.split(';')[0].split(':')[1];
    const dataUrl = canvas.toDataURL(originalMimeType);

    const cleanImageDataUrl = removeExif(dataUrl);
    return await urlToFile(cleanImageDataUrl, fileName, originalMimeType);
  } catch (error) {
    console.error('Error processing image:', error);
    return null;
  }
}
